<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, reactive, computed, watch, onMounted, nextTick } from 'vue'

const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'

import { Form as kForm, FormElement as kformelement, type FormSubmitClickEvent } from '@progress/kendo-vue-form'
import { Button } from '@progress/kendo-vue-buttons'
import { BeeBankingCreate } from '../api/Payments.ts'
import { BeeVirtualCheckCreateConnector } from '../api/VirtualCheck.ts'
import { GetBeeNumberAuthenicatedUser, GetWebConnectorUrl, GetCountryCode, GetBackOfficeUrl } from '../stores/SettingsStore.ts'
import { ApiPut } from '../api/Common.ts'
import { NewUserWizardCompleted, RenewalChangePlan } from '../api/ConsultantCorner.ts'

import type { IAoeNewUserWizard } from '../types/WizardTypes'
import { WizardSteps } from '../types/WizardTypes'
import { SetIsLoading } from '../services/AoeHelpers.ts'

// Import step components
import AoeWizardBusinessStep from './wizard/AoeWizardBusinessStep.vue'
import AoeWizardBankDetailsStep from './wizard/AoeWizardBankDetailsStep.vue'
import FrenchContributionStep from './wizard/FrenchContributionStep.vue'
import ContractAgreementStep from './wizard/ContractAgreementStep.vue'
import VatInformationStep from './wizard/VatInformationStep.vue'
import { FrenchContributionNUWUpdate, TaxExemptNUWCreate, SfaUserNUWUpdate } from '@/api/Business.ts'
import { TermsNUWCreate } from '@/api/ContractAgreement.ts'

const logger = useLogger()

// Current step in the wizard
const currentStep = ref<WizardSteps>(WizardSteps.ContractAgreement)

// Flag to prevent multiple redirects and API calls
const isRedirecting = ref(false)

// Flag: the bank account has already been saved in this attempt. Prevents creating it again
// if the user retries completion after an NUW stamp failure (last-step idempotency).
const bankSaved = ref(false)

// Flag to show loading state while validating if step should be skipped
const isValidatingStep = ref(false)

// French Contribution validation error state
const frenchContributionValidationError = ref('')

// References to step components
// Define an interface for the VatInformation component
interface VatStepInstance {
  submitVatInformation: () => Promise<void>;
}

const businessStepRef = ref<InstanceType<typeof AoeWizardBusinessStep> | null>(null)
const vatStepRef = ref<VatStepInstance | null>(null)

// Notification state
const notification = reactive({
  show: false,
  message: '',
  type: 'success' // 'success' o 'error'
})

// Function to show success messages
function showSuccessMessage(message: string) {
  notification.message = message
  notification.type = 'success'
  notification.show = true
  setTimeout(() => {
    notification.show = false
  }, 5000)
}

// Function to show error messages
function showErrorMessage(message: string) {
  notification.message = message
  notification.type = 'error'
  notification.show = true
  setTimeout(() => {
    notification.show = false
  }, 5000)
}

// Form values
const formData = reactive<IAoeNewUserWizard>({
  businessName: '',
  bankDetails: {
    bankAccountType: '',
    bankName: '',
    bic: '',
    iban: '',
    directDeposit: false,
    opa: false,
    cddCodeIdBankingType: ''
  },
  frenchContribution: {
    beeNumber: 0,
    birthCity: '',
    birthCountry: '',
    frenchCitizen: false,
    authorizedToWork: false,
    employeeStatus: '',
    ssn: '',
    siret: '',
    taxExemptionCode: '',
    codeIdExternalTaxId: 'VAT'
  },
  vatInformation: {
    vatType: 'VAT',
    beeNumber: '',
    taxExemptCode: 'STANDARD'
  },
  contractAgreement: {
    termsAccepted: false,
    termsData: null
  }
})

// Validation state for each step
const isCurrentStepValid = ref(false)

// Log when validation state changes
watch(isCurrentStepValid, (newValue: boolean) => {
  logger.debug('isCurrentStepValid changed:', newValue, 'Current step:', WizardSteps[currentStep.value])
  // Force a UI update
  nextTick(() => {
    logger.debug('After nextTick - isCurrentStepValid:', isCurrentStepValid.value)
  })
})

// Determine if we can continue to the next step
const canContinue = computed(() => {
  // Check current validation state
  const result = isCurrentStepValid.value

  // Log detailed information for debugging
  logger.debug('canContinue evaluation:', {
    step: WizardSteps[currentStep.value],
    isCurrentStepValid: isCurrentStepValid.value,
    result
  })

  // Force a UI update
  nextTick(() => {
    logger.debug('After nextTick in canContinue - isCurrentStepValid:', isCurrentStepValid.value)
  })

  return result
})

// Determine if we can go back
const canGoBack = computed(() => {
  // Can't go back from ContractAgreement (first step) or BusinessInfo (second step)
  return currentStep.value !== WizardSteps.ContractAgreement && currentStep.value !== WizardSteps.BusinessInfo
})

onMounted(() => {
  logger.debug('AoeNewUserWizard mounted')
})

// Update form data from step components
function updateFormData(newData: Partial<IAoeNewUserWizard>) {
  // Update form data with new data
  Object.assign(formData, newData)
  logger.debug('Form data updated:', formData)
}

// Specific handler for FrenchContribution step validation
function handleFrenchContributionValidation(isValid: boolean) {
  logger.debug('FrenchContribution validation received:', isValid)

  // Update validation state
  isCurrentStepValid.value = isValid

  // Force a UI update
  nextTick(() => {
    logger.debug('After nextTick in handleFrenchContributionValidation:', {
      isCurrentStepValid: isCurrentStepValid.value,
      canContinue: canContinue.value
    })
  })
}

// Specific handler for VatInformation step validation
function handleVatInformationValidation(isValid: boolean) {
  logger.debug('VatInformation validation received:', isValid)

  // Update validation state
  isCurrentStepValid.value = isValid

  // Force a UI update
  nextTick(() => {
    logger.debug('After nextTick in handleVatInformationValidation:', {
      isCurrentStepValid: isCurrentStepValid.value,
      canContinue: canContinue.value
    })
  })
}

// Handle form submission
async function onSubmitClickHandler(event: FormSubmitClickEvent) {
  logger.debug('Submit clicked:', event)

  if (!canContinue.value) {
    logger.debug('Cannot continue - validation failed')
    return
  }

  logger.debug('Form is valid, proceeding to submit')

  try {
    // If we're in the ContractAgreement step, just move to next step without validation or API call
    if (currentStep.value === WizardSteps.ContractAgreement) {
      logger.debug('Moving from ContractAgreement to BusinessInfo without validation')

      // Advance to the next step (BusinessInfo)
      currentStep.value = currentStep.value + 1 as WizardSteps
      isCurrentStepValid.value = false
      return
    }

    // If we're in the BusinessInfo step, determine next step based on CountryCode
    if (currentStep.value === WizardSteps.BusinessInfo) {
      // Check if we're navigating back - if so, skip API calls
      if (isNavigatingBack.value) {
        logger.debug('Skipping API calls during back navigation')
        SetIsLoading(false)
        return
      }

      SetIsLoading(true)

      try {
        //Get country code to determine the next step
        let countryCode = await GetCountryCode()
        // Determine the next step based on country code
        if (countryCode === 'FR') {
          // If the country is France, go to FrenchContribution step
          logger.debug('Country code is FR, moving to FrenchContribution step')
          currentStep.value = WizardSteps.FrenchContribution
        } else {
          // If not France, go to VatInformation step
          logger.debug('Country code is not FR, moving to VatInformation step')
          currentStep.value = WizardSteps.VatInformation
        }

        isCurrentStepValid.value = false
        SetIsLoading(false)
        return
      } catch (error) {
        logger.error('Error loading country code:', error)
        showErrorMessage(t('errors.loadingCountryCode'))
        SetIsLoading(false)
        return
      }
    }

    // If we're in the BankDetails step, validate and save bank details
    if (currentStep.value === WizardSteps.BankDetails) {
      // Check if we're navigating back - if so, skip API calls
      if (isNavigatingBack.value) {
        logger.debug('Skipping API calls during back navigation from BankDetails step')
        SetIsLoading(false)
        return
      }

      SetIsLoading(true)
      logger.debug('Saving bank details before proceeding')

      try {
        // Obtener el número de BEE del usuario autenticado
        const beeNumber = await GetBeeNumberAuthenicatedUser()

        // Verificar que beeNumber no sea null antes de llamar a la API
        if (beeNumber === null) {
          throw new Error(t('errors.userNotAuthenticatedOrInvalidBeeNumber'))
        }

        // The bank account is saved only once. If the user retries completion after an NUW
        // stamp failure, the bank account is NOT created again (avoids duplicates).
        if (!bankSaved.value) {
          // BeeBanking must always be saved
          logger.debug('Calling BeeBankingCreate for bank details save')
          await BeeBankingCreate(beeNumber, {
            ...formData.bankDetails,
            // Map the form's account type into the field BeeBankingCreate reads,
            // otherwise CddCodeIdAccountType is undefined and the row is written
            // with a null cddCodeGuidAccountType.
            cddCodeIdAccountType: formData.bankDetails.bankAccountType
          })
          logger.debug('Bank details saved successfully in BeeBanking')

          // If OPA is selected, also save in BeeVirtualCheck
          if (formData.bankDetails.opa) {
            logger.debug('OPA selected, calling BeeVirtualCheckCreateConnector')

            console.log(formData.bankDetails.bankAccountType)

            const virtualCheckRequest = {
              BeeNumber: beeNumber,
              CddCodeIdAccountType: formData.bankDetails.bankAccountType,
              BankName: formData.bankDetails.bankName,
              AbaNumber: formData.bankDetails.bic,
              AccountNumber: formData.bankDetails.iban.replace(/\s/g, '').toUpperCase(),
              BeeActivated: 0,
              BeeActivatedDate: null,
              IsEnabled: 0,
              IsVerified: 0
            }

            await BeeVirtualCheckCreateConnector(virtualCheckRequest)
            logger.debug('Virtual check created successfully')
          }

          bankSaved.value = true
          showSuccessMessage(t('messages.bankDetailsSaved'))
        } else {
          logger.debug('Bank already saved in a previous attempt; skipping create and retrying completion only')
        }

        // Since BankDetails is now the last step, complete the wizard and redirect.
        // Only redirects if the NUW stamp succeeds (see completeWizardAndRedirect).
        await completeWizardAndRedirect(beeNumber, 'Bank details')
        return
      } catch (error) {
        logger.error('Error saving bank details:', error)
        showErrorMessage(t('errors.savingBankDetails'))
        SetIsLoading(false)
        return
      }
    }

    // If we're in the French Contribution step, validate and save data before continuing
    if (currentStep.value === WizardSteps.FrenchContribution) {
      // Check if we're navigating back - if so, skip API calls
      if (isNavigatingBack.value) {
        logger.debug('Skipping API calls during back navigation from FrenchContribution step')
        SetIsLoading(false)
        return
      }

      SetIsLoading(true)
      logger.debug('Saving French contribution and VAT data before proceeding')

      try {
        // Obtener el número de BEE del usuario autenticado
        const beeNumber = await GetBeeNumberAuthenicatedUser()

        // Verificar que beeNumber no sea null antes de llamar a la API
        if (beeNumber === null) {
          throw new Error(t('errors.userNotAuthenticatedOrInvalidBeeNumber'))
        }

        // First, save the VAT data (only if VAT is selected, not TAXID)
        try {
          // Extract just the value if it's an object
          const vatTypeValue = typeof formData.frenchContribution.codeIdExternalTaxId === 'object' && formData.frenchContribution.codeIdExternalTaxId !== null
            ? (formData.frenchContribution.codeIdExternalTaxId as any).value
            : formData.frenchContribution.codeIdExternalTaxId

          // Only call API if VAT is selected, skip if TAXID is selected
          if (vatTypeValue === 'VAT') {
            // Prepare the VAT request data
            const vatRequestData = {
              BeeNumber: beeNumber.toString(),
              TaxExemptCode: formData.frenchContribution.taxExemptionCode,
              CodeIdExtendedTaxId: vatTypeValue
            }

            logger.debug('Submitting VAT information:', vatRequestData)

            // Call the API to save the VAT information
            await TaxExemptNUWCreate(vatRequestData)

            logger.debug('VAT information saved successfully')
          } else {
            // TAXID selected - skip API call
            logger.debug('TAXID selected - skipping TaxExemptNUWCreate API call for French contribution')
          }
        } catch (vatError) {
          logger.error('Error saving VAT information:', vatError)
          frenchContributionValidationError.value = t('errors.savingVatInformation')
          SetIsLoading(false)
          return
        }

        // Then, save the French contribution data
        const requestData = {
          BeeNumber: beeNumber,
          BirthCity: formData.frenchContribution.birthCity,
          BirthCountry: formData.frenchContribution.birthCountry,
          FrenchCitizen: formData.frenchContribution.frenchCitizen,
          AuthorizedToWork: formData.frenchContribution.authorizedToWork,
          EmployeeStatus: formData.frenchContribution.employeeStatus,
          SSN: formData.frenchContribution.ssn,
          SIRET: formData.frenchContribution.siret
        }

        // Call API to save French Contribution data
        const result = await FrenchContributionNUWUpdate(requestData);

        // Validate response status for errors
        if (result && result.responseStatus && result.responseStatus.errorCode) {
          logger.error('French contribution API returned error:', result.responseStatus.errorCode)

          // Check for specific government ID invalid error
          if (result.responseStatus.errorCode === 'GovernmentIdInvalidException') {
            frenchContributionValidationError.value = t('errors.governmentIdInvalid')
          } else {
            // Show generic error for other error codes
            frenchContributionValidationError.value = t('errors.savingFrenchContribution')
          }

          SetIsLoading(false)
          return // Prevent navigation to next step
        }

        showSuccessMessage(t('messages.frenchContributionSaved'))
        logger.debug('French contribution data saved successfully', result)

        // After saving French contribution data, move to BankDetails step
        currentStep.value = WizardSteps.BankDetails
        isCurrentStepValid.value = false
        SetIsLoading(false)
        return
      } catch (error) {
        logger.error('Error saving French contribution data:', error)
        showErrorMessage(t('errors.savingFrenchContribution'))
        SetIsLoading(false)
        return
      }
    }

    // If we're in the VAT Information step, validate and save data before continuing
    if (currentStep.value === WizardSteps.VatInformation) {
      // Check if we're navigating back - if so, skip API calls
      if (isNavigatingBack.value) {
        logger.debug('Skipping API calls during back navigation from VatInformation step')
        SetIsLoading(false)
        return
      }

      SetIsLoading(true)
      logger.debug('Saving VAT information data before proceeding')

      try {
        // Ensure we're only using the value, not the object
        const vatType = typeof formData.vatInformation.vatType === 'object' && formData.vatInformation.vatType !== null
          ? (formData.vatInformation.vatType as any).value
          : formData.vatInformation.vatType

        // Only call API if VAT is selected, skip if TAXID is selected
        if (vatType === 'VAT') {
          // Obtener el número de BEE del usuario autenticado
          const beeNumber = await GetBeeNumberAuthenicatedUser()

          // Verificar que beeNumber no sea null antes de llamar a la API
          if (beeNumber === null) {
            throw new Error(t('errors.userNotAuthenticatedOrInvalidBeeNumber'))
          }

          // Prepare the request data with the correct field mappings
          const requestData = {
            BeeNumber: beeNumber.toString(), // The authenticated user's BEE number
            TaxExemptCode: formData.vatInformation.taxExemptCode, // The VAT number entered by the user (e.g., DE123456789)
            CodeIdExtendedTaxId: vatType // The selected VAT type (VAT or TAXID)
          }

          logger.debug('VAT Information request data:', requestData)

          // Llamar a la API para guardar los datos de VAT Information
          await TaxExemptNUWCreate(requestData);

          showSuccessMessage(t('messages.vatInformationSaved'))
          logger.debug('VAT information data saved successfully')
        } else {
          // TAXID selected - skip API call
          logger.debug('TAXID selected - skipping API call in parent wizard')
        }

        // After saving (or skipping) VAT information data, move to BankDetails step
        currentStep.value = WizardSteps.BankDetails
        isCurrentStepValid.value = false
        SetIsLoading(false)
        return
      } catch (error) {
        logger.error('Error saving VAT information data:', error)
        showErrorMessage(t('errors.savingVatInformation'))
        SetIsLoading(false)
        return
      }
    }

    // En este punto, todos los casos especiales ya han sido manejados

    // Enviar los datos al backend (último paso)
    submitFormToBackend()
  } catch (error) {
    logger.error('Error processing step:', error)

    // Mostrar mensaje de error según el contexto
    if (currentStep.value === WizardSteps.BankDetails) {
      showErrorMessage(t('errors.savingBankDetails'))
      SetIsLoading(false)
    }
  }

  // Log form values
  logger.debug('Form values:', formData)
}

// Go to previous step
// Flag to track if we're navigating back (to prevent API calls)
const isNavigatingBack = ref(false)

function onBackClick() {
  logger.debug('Back button clicked')
  if (canGoBack.value) {
    // Set flag to indicate we're navigating back
    isNavigatingBack.value = true

    // Prevent automatic API calls when going back
    isValidatingStep.value = true

    // Store the previous step before changing
    const previousStep = currentStep.value

    // Update the current step
    currentStep.value = currentStep.value - 1 as WizardSteps

    // If we're going back from VatInformation, we need to determine the correct previous step
    if (previousStep === WizardSteps.VatInformation) {
      // Check country code to determine if we should go to FrenchContribution or BusinessInfo
      GetCountryCode().then(countryCode => {
        if (countryCode === 'FR') {
          // If country is France, we should go to FrenchContribution step
          logger.debug('Going back from VatInformation to FrenchContribution (FR country)')
          currentStep.value = WizardSteps.FrenchContribution

          // Reset validation state for FrenchContribution step based on existing data
          nextTick(() => {
            // Check if the French contribution step has required fields filled
            const hasContent =
              formData.frenchContribution.birthCity.trim() !== '' ||
              formData.frenchContribution.birthCountry.trim() !== '' ||
              formData.frenchContribution.employeeStatus.trim() !== '' ||
              formData.frenchContribution.siret.trim() !== '' ||
              formData.frenchContribution.ssn.trim() !== ''

            const authorizedToWorkValid =
              formData.frenchContribution.frenchCitizen ||
              formData.frenchContribution.authorizedToWork

            // Update validation state based on existing data
            isCurrentStepValid.value = hasContent && authorizedToWorkValid

            // Complete the validation process
            isValidatingStep.value = false

            // Reset the back navigation flag after a short delay
            setTimeout(() => {
              isNavigatingBack.value = false
            }, 500)

            logger.debug('Back navigation validation complete for FrenchContribution', {
              hasContent,
              authorizedToWorkValid,
              isCurrentStepValid: isCurrentStepValid.value
            })
          })
        } else {
          // If not France, go directly to BusinessInfo step (skip FrenchContribution)
          logger.debug('Going back from VatInformation to BusinessInfo (non-FR country)')
          currentStep.value = WizardSteps.BusinessInfo

          // Reset validation state for BusinessInfo step based on existing data
          if (businessStepRef.value) {
            // We'll validate the step in the next tick after the component is updated
            nextTick(() => {
              // Check if the business step has required fields filled
              const hasBusinessName = Boolean(formData.businessName && formData.businessName.trim() !== '')

              // Update validation state based on existing data
              isCurrentStepValid.value = hasBusinessName

              // Complete the validation process
              isValidatingStep.value = false

              // Reset the back navigation flag after a short delay
              setTimeout(() => {
                isNavigatingBack.value = false
              }, 500)

              logger.debug('Back navigation validation complete for BusinessInfo', {
                hasBusinessName,
                isCurrentStepValid: isCurrentStepValid.value
              })
            })
          } else {
            isValidatingStep.value = false
            // Reset the back navigation flag
            setTimeout(() => {
              isNavigatingBack.value = false
            }, 500)
          }
        }
      }).catch(error => {
        logger.error('Error getting country code for back navigation from VatInformation:', error)
        // Default to BusinessInfo if there's an error
        currentStep.value = WizardSteps.BusinessInfo
        isValidatingStep.value = false
        setTimeout(() => {
          isNavigatingBack.value = false
        }, 500)
      })
    }
    // If we're going back from BankDetails, we need to determine the correct previous step
    else if (previousStep === WizardSteps.BankDetails) {
      // Check country code to determine if we should go to FrenchContribution or VatInformation
      GetCountryCode().then(countryCode => {
        if (countryCode === 'FR') {
          // If country is France, we should go to FrenchContribution step
          logger.debug('Going back from BankDetails to FrenchContribution (FR country)')
          currentStep.value = WizardSteps.FrenchContribution

          // Reset validation state for FrenchContribution step based on existing data
          nextTick(() => {
            // Check if the French contribution step has required fields filled
            const hasContent =
              formData.frenchContribution.birthCity.trim() !== '' ||
              formData.frenchContribution.birthCountry.trim() !== '' ||
              formData.frenchContribution.employeeStatus.trim() !== '' ||
              formData.frenchContribution.siret.trim() !== '' ||
              formData.frenchContribution.ssn.trim() !== ''

            const authorizedToWorkValid =
              formData.frenchContribution.frenchCitizen ||
              formData.frenchContribution.authorizedToWork

            // Update validation state based on existing data
            isCurrentStepValid.value = hasContent && authorizedToWorkValid

            // Complete the validation process
            isValidatingStep.value = false

            // Reset the back navigation flag after a short delay
            setTimeout(() => {
              isNavigatingBack.value = false
            }, 500)

            logger.debug('Back navigation validation complete for FrenchContribution', {
              hasContent,
              authorizedToWorkValid,
              isCurrentStepValid: isCurrentStepValid.value
            })
          })
        } else {
          // If not France, go to VatInformation step (original logic)
          logger.debug('Going back from BankDetails to VatInformation (non-FR country)')
          currentStep.value = WizardSteps.VatInformation

          // Reset validation state for VatInformation step based on existing data
          if (vatStepRef.value) {
            // We'll validate the step in the next tick after the component is updated
            nextTick(() => {
              // Check if the VAT information step has required fields filled
              const hasVatType = Boolean(formData.vatInformation.vatType)
              const hasTaxExemptCode = Boolean(formData.vatInformation.taxExemptCode && formData.vatInformation.taxExemptCode.trim() !== '')

              // Update validation state based on existing data
              isCurrentStepValid.value = hasVatType && hasTaxExemptCode

              // Complete the validation process
              isValidatingStep.value = false

              // Reset the back navigation flag after a short delay
              setTimeout(() => {
                isNavigatingBack.value = false
              }, 500)

              logger.debug('Back navigation validation complete for VatInformation', {
                hasVatType,
                hasTaxExemptCode,
                isCurrentStepValid: isCurrentStepValid.value
              })
            })
          } else {
            isValidatingStep.value = false
            // Reset the back navigation flag
            setTimeout(() => {
              isNavigatingBack.value = false
            }, 500)
          }
        }
      }).catch(error => {
        logger.error('Error getting country code for back navigation:', error)
        // Default to VatInformation if there's an error
        currentStep.value = WizardSteps.VatInformation
        isValidatingStep.value = false
        setTimeout(() => {
          isNavigatingBack.value = false
        }, 500)
      })
    } else {
      isValidatingStep.value = false
      // Reset the back navigation flag
      setTimeout(() => {
        isNavigatingBack.value = false
      }, 500)
    }
  }
}

// Helper function to complete the wizard and redirect to account page
// Function to handle step completion and move to the next step
async function onStepComplete(nextStep?: WizardSteps) {
  logger.debug('Step complete, moving to next step')

  // Ensure loading states are cleared
  isValidatingStep.value = false
  SetIsLoading(false)

  // If a specific next step is provided, go to that step
  if (nextStep !== undefined) {
    logger.debug(`Moving to specific step: ${WizardSteps[nextStep]}`)
    currentStep.value = nextStep
  } else {
    // Otherwise, move to the next step in sequence
    logger.debug(`Moving to next step in sequence: ${WizardSteps[currentStep.value + 1]}`)
    currentStep.value = currentStep.value + 1 as WizardSteps
  }

  // Reset validation state for the new step
  isCurrentStepValid.value = false

  // Force a UI update
  await nextTick()
}

async function completeWizardAndRedirect(beeNumber: number | string, completedStep: string) {
  if (isRedirecting.value) {
    logger.debug('Already redirecting, preventing duplicate calls')
    return
  }

  isRedirecting.value = true

  // Last-step atomicity: the NUW stamp must be confirmed BEFORE taking the user out of the
  // wizard. Retried a few times to absorb transient failures (timeout, expired token, etc.).
  const MAX_ATTEMPTS = 3
  let completed = false
  for (let attempt = 1; attempt <= MAX_ATTEMPTS && !completed; attempt++) {
    try {
      logger.debug(`Calling NewUserWizardCompleted API (attempt ${attempt}/${MAX_ATTEMPTS}) with beeNumber:`, beeNumber)
      await NewUserWizardCompleted({ beeNumber })
      completed = true
      logger.debug('Wizard completion status updated successfully')
    } catch (updateError) {
      logger.error(`NewUserWizardCompleted failed (attempt ${attempt}/${MAX_ATTEMPTS}):`, updateError)
      if (attempt < MAX_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt))
      }
    }
  }

  if (!completed) {
    // The stamp was NOT confirmed: do NOT redirect. The user stays on the last step and can
    // retry (the already-saved bank account is not created again). This way they never end up
    // in an inconsistent state (bank saved + NUW null + treated as customer).
    logger.error('Wizard completion could not be confirmed; keeping user in wizard for retry')
    showErrorMessage(t('errors.wizardCompletionRetry'))
    isRedirecting.value = false
    SetIsLoading(false)
    return
  }

  // Stamp confirmed. The following steps are not critical for completion.
  try {
    // Auto-enroll in the Free Trial program for every country except FR (PWS/Trial does not apply to FR)
    const countryCode = (await GetCountryCode())?.toUpperCase()
    if (countryCode !== 'FR') {
      try {
        await RenewalChangePlan({ newProgramId: 'Trial', frequency: undefined })
        logger.debug('Auto-enrolled in Free Trial for country', countryCode)
      } catch (trialError) {
        logger.error('Auto free trial enrollment failed, continuing wizard completion:', trialError)
      }
    }
  } catch (postError) {
    logger.error('Non-critical post-completion step failed, continuing:', postError)
  }

  // Show success message
  showSuccessMessage(t('messages.wizardCompleted'))

  // Redirect to account page after completing the wizard
  logger.debug(`${completedStep} completed, redirecting to account page`)

  const backOfficeUrl = await GetBackOfficeUrl()
  const accountUrl = `${backOfficeUrl}?newrsbnu=${beeNumber}`

  // Add a small delay to show the success message before redirecting
  setTimeout(() => {
    window.location.href = accountUrl
  }, 1500)
}

// Send data to backend
async function submitFormToBackend() {
  SetIsLoading(true)

  try {
    // Get the BEE number of the authenticated user
    const beeBusinessGuid = await GetBeeNumberAuthenicatedUser()


    if (beeBusinessGuid === null) {
      throw new Error(t('errors.userNotAuthenticatedOrInvalidBusinessGuid'))
    }

    const bankingResult = await BeeBankingCreate(beeBusinessGuid, {
      bankAccountType: formData.bankDetails.bankAccountType,
      bankName: formData.bankDetails.bankName,
      bic: formData.bankDetails.bic,
      iban: formData.bankDetails.iban,
      directDeposit: formData.bankDetails.directDeposit,
      opa: formData.bankDetails.opa,
      cddCodeIdBankingType: 'PAYMENT'
    })
    if (!bankingResult) throw new Error(t('errors.failedToSaveBankDetails'))

    showSuccessMessage(t('messages.bankDetailsSaved'))

    const frenchContributionUrl = `${await GetWebConnectorUrl()}/api/FrenchContribution/${beeBusinessGuid}`
    const frenchContributionResult = await ApiPut(frenchContributionUrl, formData.frenchContribution)
    if (!frenchContributionResult) throw new Error(t('errors.failedToSaveFrenchContributionData'))

    showSuccessMessage(t('messages.frenchContributionSaved'))

    showSuccessMessage(t('messages.wizardCompleted'))

    console.log('Form submitted successfully')

  } catch (error) {
    logger.error('Error in form submission:', error)

    if (String(error).includes('bank')) {
      showErrorMessage(t('errors.savingBankDetails'))
    } else if (String(error).includes('French contribution')) {
      showErrorMessage(t('errors.savingFrenchContribution'))
    } else {
      showErrorMessage(String(error))
    }
  } finally {
    SetIsLoading(false)
  }
}
</script>

<template>
  <div class="aoe-new-user-wizard">
    <!-- Notification component -->
    <div v-if="notification.show" :class="['notification', notification.type]">
      {{ notification.message }}
    </div>
    <kForm
      :initialValues="formData"
      @submitclick="onSubmitClickHandler($event)">
      <kformelement>
        <div class="wizard-container">
          <!-- Loading overlay while validating step -->
          <div v-if="isValidatingStep" class="loading-overlay">
            <div class="loading-spinner"></div>
          </div>
          <div class="wizard-header">
            <h2>{{ t('labels.NewUserWizardHeader') }}</h2>
            <p>{{ t('labels.NewUserWizardSubHeader') }}</p>
          </div>

          <!-- Renderizar el paso actual del wizard -->
          <AoeWizardBusinessStep
            v-if="currentStep === WizardSteps.BusinessInfo"
            :formData="formData"
            :onUpdate="updateFormData"
            :isValid="isCurrentStepValid"
            :onStepComplete="onStepComplete"
            @update:isValid="isCurrentStepValid = $event"
            @skip-french-contribution="currentStep = WizardSteps.ContractAgreement"
            @skip-to-step="(step) => { currentStep = step; isCurrentStepValid = false; }"
            ref="businessStepRef"
          />

          <AoeWizardBankDetailsStep
            v-if="currentStep === WizardSteps.BankDetails"
            :formData="formData"
            :onUpdate="updateFormData"
            :isValid="isCurrentStepValid"
            :onStepComplete="onStepComplete"
            @update:isValid="isCurrentStepValid = $event"
            @skip-french-contribution="currentStep = WizardSteps.ContractAgreement"
            @skip-to-step="(step) => { currentStep = step; isCurrentStepValid = false; }"
          />

          <FrenchContributionStep
            v-if="currentStep === WizardSteps.FrenchContribution"
            :formData="formData"
            :onUpdate="updateFormData"
            :isValid="isCurrentStepValid"
            :onStepComplete="onStepComplete"
            :validationError="frenchContributionValidationError"
            @update:isValid="handleFrenchContributionValidation"
            @clearValidationError="frenchContributionValidationError = ''"
          />

          <VatInformationStep
            v-if="currentStep === WizardSteps.VatInformation"
            :formData="formData"
            :onUpdate="updateFormData"
            :isValid="isCurrentStepValid"
            :onStepComplete="onStepComplete"
            @update:isValid="isCurrentStepValid = $event"
            @skip-to-step="(step) => { currentStep = step; isCurrentStepValid = false; }"
            ref="vatStepRef"
          />

          <ContractAgreementStep
            v-if="currentStep === WizardSteps.ContractAgreement"
            :formData="formData"
            :onUpdate="updateFormData"
            :isValid="isCurrentStepValid"
            :onStepComplete="onStepComplete"
            @update:isValid="isCurrentStepValid = $event"
            @skip-to-step="(step) => { currentStep = step; isCurrentStepValid = false; }"
          />

          <div class="wizard-actions">
            <Button
              v-if="canGoBack"
              @click="onBackClick"
              class="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
            >
              {{ t('buttons.Back') }}
            </Button>
            <Button
              :disabled="!canContinue"
              type="submit"
              class="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
            >
              {{ t('buttons.Continue') }}
            </Button>
          </div>
        </div>
      </kformelement>
    </kForm>
  </div>
</template>

<style lang="scss">
.aoe-new-user-wizard {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: $card-padding;
  @include typography-small;
  position: relative;
}

.wizard-container {
  background-color: var(--vt-c-white);
  border-radius: $radius-md;
  box-shadow: 0 2px 10px rgba(black, 0.1);
  padding: $spacing-8;
}

.wizard-header {
  text-align: center;
  margin-bottom: 25px;
}

.wizard-header h2 {
  @include typography-h3;
  @include font-weight-semibold;
  margin-bottom: 8px;
}

.wizard-header p {
  @include typography-body;
  color: #555;
  margin: 0;
}

/* Estilos globales para los títulos de los pasos del wizard */
.wizard-step h3 {
  @include typography-body-large;
  @include font-weight-semibold;
  margin-bottom: 8px;
}

.wizard-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.wizard-actions:has(> :only-child) {
  justify-content: flex-end;
}

/* Notification styles */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: $spacing-4 $spacing-5;
  border-radius: $radius-sm;
  @include typography-body;
  @include font-weight-medium;
  color: white;
  z-index: $z-notification;
  box-shadow: 0 4px 8px rgba(black, 0.1);
  animation: fadeIn 0.3s ease-in-out;
}

.notification.success {
  background-color: #4caf50;
}

.notification.error {
  background-color: #f44336;
}

/* Loading overlay styles */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(white, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: $radius-md;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(black, 0.1);
  border-radius: 50%;
  border-top-color: #3498db;
  animation: spin 1s ease-in-out infinite;
}

/* Global styles for all wizard steps */
:deep(.wizard-step-header h3) {
  @include typography-h4;
  @include font-weight-semibold;
  margin-bottom: 8px;
}

:deep(.wizard-step-header p) {
  @include typography-body;
  color: #555;
  margin: 0 0 15px 0;
}

:deep(.form-label) {
  @include typography-body;
  @include font-weight-medium;
  margin-bottom: 6px;
  display: inline-block;
  min-width: 120px;
}

:deep(.form-row) {
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
}

:deep(.form-field-container) {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
}

:deep(.k-dropdown),
:deep(.k-textbox),
:deep(.k-textarea) {
  @include typography-body;
  width: 100%;
}

:deep(.help-text) {
  @include typography-small;
  color: #666;
  margin-top: 4px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
